import { useCallback, useMemo, useState } from 'react'
import { CSATFeedbackCollapse } from 'Components/common/CSATFeedbackCollapse/CSATFeedbackCollapse'
import type { CSATFeedbackValue } from 'Components/common/CSATFeedbackCollapse/CSATFeedbackCollapse'
import type {
    CSATFeedbackConfig,
    CSATQuestionConfig,
    CSATReactionResponse,
    CSATFeedbackData,
} from 'Types/CSAT.types'

export const useCSATFeedback = (feedbackConfig?: CSATFeedbackConfig) => {
    const [isSurveyExpanded, setIsSurveyExpanded] = useState(false)
    const [surveyRating, setSurveyRating] = useState<number | null>(null)
    const [reactionChoices, setReactionChoices] = useState<Record<string, 'yes' | 'no' | null>>({})
    const [surveyComment, setSurveyComment] = useState<string>('')

    const csatValue: CSATFeedbackValue = useMemo(
        () => ({
            expanded: isSurveyExpanded,
            rating: surveyRating,
            comment: surveyComment,
            choices: reactionChoices,
        }),
        [isSurveyExpanded, surveyRating, surveyComment, reactionChoices]
    )

    const handleCSATChange = useCallback((next: CSATFeedbackValue) => {
        setIsSurveyExpanded(next.expanded)
        setSurveyRating(next.rating)
        setSurveyComment(next.comment)
        setReactionChoices(next.choices)
    }, [])

    const resetCSATFeedback = useCallback(() => {
        setIsSurveyExpanded(false)
        setSurveyRating(null)
        setReactionChoices({})
        setSurveyComment('')
    }, [])

    const getReactionResponses = useCallback(
        (surveyReactions: CSATQuestionConfig[]): CSATReactionResponse[] => {
            return surveyReactions.flatMap(q => {
                const choice = reactionChoices[q.question_title] ?? null
                if (choice === null) return []
                return [
                    {
                        question_title: q.question_title,
                        question_text: q.question_text,
                        reaction: choice === 'yes',
                        is_core: q.is_core,
                    },
                ]
            })
        },
        [reactionChoices]
    )

    const getFeedbackValidation = useCallback(
        (reactions: CSATReactionResponse[], isCommentEnabled: boolean) => {
            const hasAnyReactions = reactions.length > 0
            const commentTrimmed = surveyComment.trim()
            const hasComment = isCommentEnabled && commentTrimmed.length > 0
            const hasFeedback = hasAnyReactions || hasComment
            const hasRating = surveyRating != null

            return {
                hasAnyReactions,
                commentTrimmed,
                hasComment,
                hasFeedback,
                hasRating,
            }
        },
        [surveyComment, surveyRating]
    )

    const getFeedbackData = useCallback((): {
        feedbackData?: CSATFeedbackData
        error?: string
    } => {
        if (!feedbackConfig || !feedbackConfig.is_enabled) {
            return {}
        }

        const reactions = getReactionResponses(feedbackConfig.reaction_questions)
        const { hasAnyReactions, commentTrimmed, hasComment, hasFeedback, hasRating } =
            getFeedbackValidation(reactions, feedbackConfig.comment_enabled)

        if (hasFeedback && !hasRating) {
            return { error: 'Please select a rating before submitting feedback' }
        }

        if (hasRating) {
            return {
                feedbackData: {
                    rating: surveyRating!,
                    comment: hasComment ? commentTrimmed : undefined,
                    reactions: hasAnyReactions ? reactions : undefined,
                },
            }
        }

        return {}
    }, [feedbackConfig, getReactionResponses, getFeedbackValidation, surveyRating])

    const csatSurveyUI =
        !feedbackConfig || feedbackConfig.type !== 'CSAT' || !feedbackConfig.is_enabled ? null : (
            <CSATFeedbackCollapse
                feedbackConfig={feedbackConfig}
                value={csatValue}
                onChange={handleCSATChange}
            />
        )

    return {
        csatSurveyUI,
        resetCSATFeedback,
        getFeedbackData,
    }
}
